/**
 * The services piece's behavior half: reads the catalog, applies the options, opens a service. With the showTimes
 * option it also shows each listed service's next open times in the visitor's zone, from the studio's one shared
 * answer, and opens one (web-studio-sites D15). With the mine option, marks each service a signed-in client has booked with their next booking of it, from the
 * dashboard read, and opens that booking (6.6). Draws nothing.
 */
import { useMemo } from 'react';
import { groupOpenStarts, type SiteServiceSummaryDto } from '@telify/shared';

import { useNextOpeningsFor, useOpenTimes, useSiteServices } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone, slotTimeLabels, zoneName } from '../../format';
import { useWebDashboard } from '../../hooks/useWebAccount';
import { serviceMarks } from '../../mine';
import { usePageMover } from '../../pageMover';
import { wholeCount } from '../../wholeCount';
import {
  contract,
  type ServiceOpenDay,
  type ServicesActions,
  type ServicesEvents,
  type ServicesState,
  type ServiceTimes,
} from './contract';

/** The services page, every service the studio lists. */
const ALL_SERVICES_PATH = '/services';

const MAX_DAYS = 4;
const MAX_TIMES = 4;

export type ServicesRuntime = PieceRuntime<typeof contract, ServicesState, ServicesActions>;

/** A calendar day (YYYY-MM-DD) formatted on its own, with no zone shift. */
function formatDate(date: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
}

function timeHref(service: SiteServiceSummaryDto, startsAt: string): string {
  return `${service.path}?start=${encodeURIComponent(startsAt)}`;
}

/** A service's start instants as the visitor's first days with openings, up to four times each, gone ones left out. */
function buildDays(service: SiteServiceSummaryDto, starts: readonly string[], tz: string, now: number): ServiceOpenDay[] {
  return groupOpenStarts(starts, tz, now, { days: MAX_DAYS, perDay: MAX_TIMES }).map((d) => {
    const labels = slotTimeLabels(d.starts, tz);
    return {
      date: d.date,
      name: formatDate(d.date, { weekday: 'short' }),
      dateLabel: formatDate(d.date, { month: 'short', day: 'numeric' }),
      longLabel: formatDate(d.date, { weekday: 'long', month: 'long', day: 'numeric' }),
      times: d.starts.map((startsAt, i) => ({ startsAt, label: labels[i]!, href: timeHref(service, startsAt) })),
    };
  });
}

export function useServices(input: PieceInput<ServicesEvents> = {}): ServicesRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const tz = useMemo(() => getClientTimezone(), []);
  const query = useSiteServices();

  // 0 (the default) shows every service the studio lists.
  const all = useMemo(() => query.data ?? [], [query.data]);
  const limit = wholeCount(options.limit);
  const services = useMemo(() => (limit > 0 ? all.slice(0, limit) : all), [all, limit]);
  const status: ServicesState['status'] = query.isError ? 'error' : query.isPending ? 'loading' : 'ready';

  // With the showTimes option, one read for every listed service's times: the studio's shared answer (task 6.7). Only
  // a listed service it lacks, such as one a signed-in client's tag unlocks, reads its own next openings.
  const shared = useOpenTimes(options.showTimes);
  const sharedStarts = useMemo(() => new Map((shared.data ?? []).map((s) => [s.serviceId, s.starts])), [shared.data]);
  const unshared = useMemo(
    () => (options.showTimes && shared.isSuccess ? services.filter((s) => !sharedStarts.has(s.id)) : []),
    [options.showTimes, shared.isSuccess, services, sharedStarts],
  );
  const own = useNextOpeningsFor(unshared.map((s) => s.id), tz);
  const times = useMemo(() => {
    const out: Record<string, ServiceTimes> = {};
    if (!options.showTimes) return out;
    const now = Date.now();
    const sharedStatus = shared.isError ? 'error' : shared.isPending ? 'loading' : 'ready';
    for (const service of services) {
      const starts = sharedStarts.get(service.id);
      out[service.id] = { status: sharedStatus, days: starts ? buildDays(service, starts, tz, now) : [] };
    }
    own.forEach((q, i) => {
      const service = unshared[i]!;
      out[service.id] = {
        status: q.isError ? 'error' : q.isPending ? 'loading' : 'ready',
        days: q.data ? buildDays(service, q.data.days.flatMap((d) => d.times.map((t) => t.startsAt)), tz, now) : [],
      };
    });
    return out;
  }, [options.showTimes, shared.isError, shared.isPending, sharedStarts, own, unshared, services, tz]);

  // The one shared dashboard read, held without the mine option; a guest makes none.
  const upcoming = useWebDashboard({ enabled: options.mine }).data?.upcoming;
  const marks = useMemo(() => (options.mine && upcoming ? serviceMarks(upcoming, new Date(), tz) : {}), [options.mine, upcoming, tz]);

  const actions = useMemo<ServicesActions>(
    () => ({
      open: (serviceId) => {
        const service = services.find((s) => s.id === serviceId);
        if (!service) return;
        emit('opened', { serviceId });
        navigate(service.path);
      },
      seeAll: () => navigate(ALL_SERVICES_PATH),
      openBooking: (serviceId) => {
        const mark = marks[serviceId];
        if (mark) navigate(mark.action.href);
      },
      openTime: (serviceId, start) => {
        const service = services.find((s) => s.id === serviceId);
        // Only one of this service's shown times: another service's, a gone or a made-up start does nothing.
        const time = times[serviceId]?.days.flatMap((d) => d.times).find((t) => t.startsAt === start);
        if (!service || !time) {
          console.error(`[telify] services.openTime: "${start}" is not one of ${serviceId}'s open times; nothing done.`);
          return;
        }
        emit('opened', { serviceId });
        navigate(time.href);
      },
    }),
    [services, times, marks, emit, navigate],
  );

  return {
    state: {
      status,
      services,
      total: all.length,
      hasMore: services.length < all.length,
      allServicesPath: ALL_SERVICES_PATH,
      times,
      timezone: tz,
      zoneName: zoneName(tz),
      marks,
    },
    actions,
    options,
  };
}
